/**
 * Enable tap-to-flip behavior for discipline cards on touch screens.
 *
 * Desktop keeps the CSS hover interaction from the design comp, while touch
 * users need a deliberate tap state because hover content is otherwise hidden.
 *
 * @returns {void}
 */
export function initCardsDisciplineGrid() {
	const cards = document.querySelectorAll( '.cards-discipline-grid-card' );

	if ( cards.length === 0 ) {
		return;
	}

	const touchQuery = window.matchMedia( '(hover: none) and (pointer: coarse)' );

	const getCardLink = function ( card ) {
		return card.querySelector( '.cards-discipline-grid-card-cta .wp-block-button__link' );
	};

	const isBareExternalUrl = function ( href ) {
		return /^(?:[a-z0-9-]+\.)+[a-z]{2,}(?:[/:?#]|$)/i.test( href );
	};

	const getCardLinkUrl = function ( link ) {
		const href = link ? ( link.getAttribute( 'href' ) || '' ).trim() : '';

		if ( ! href || href === '#' || /^(?:javascript|data):/i.test( href ) ) {
			return '';
		}

		let normalizedHref = href;
		const hasProtocol = /^[a-z][a-z0-9+.-]*:/i.test( href );

		if ( href.startsWith( '//' ) ) {
			normalizedHref = 'https:' + href;
		} else if ( hasProtocol && ! /^https?:/i.test( href ) ) {
			return '';
		} else if ( ! hasProtocol && isBareExternalUrl( href ) ) {
			normalizedHref = 'https://' + href;
		}

		try {
			const url = new URL( normalizedHref, window.location.href );

			if ( url.protocol === 'http:' ) {
				url.protocol = 'https:';
			}

			return url.toString();
		} catch {
			return link.href || '';
		}
	};

	const normalizeCardLink = function ( link ) {
		const url = getCardLinkUrl( link );

		if ( url && link.href !== url ) {
			link.href = url;
		}

		return url;
	};

	const getCardTitle = function ( card ) {
		const title = card.querySelector( '.cards-discipline-grid-card-back h3, .cards-discipline-grid-card-front h3' );

		return title ? title.textContent.trim().replace( /\s+/g, ' ' ) : '';
	};

	const followCardLink = function ( card ) {
		const link = getCardLink( card );

		if ( ! link || ! link.href ) {
			return;
		}

		const url = normalizeCardLink( link );

		if ( ! url ) {
			return;
		}

		if ( link.target === '_blank' ) {
			window.open( url, '_blank', 'noopener' );
			return;
		}

		window.location.href = url;
	};

	const setMobileAttributes = function () {
		cards.forEach( function ( card ) {
			const cardLink = getCardLink( card );

			if ( cardLink ) {
				normalizeCardLink( cardLink );
			}

			if ( touchQuery.matches ) {
				card.setAttribute( 'role', 'button' );
				card.setAttribute( 'tabindex', '0' );
				card.setAttribute( 'aria-expanded', card.classList.contains( 'is-flipped' ) ? 'true' : 'false' );
				card.removeAttribute( 'aria-label' );
				return;
			}

			card.classList.remove( 'is-flipped' );
			card.setAttribute( 'role', cardLink ? 'link' : 'group' );
			card.setAttribute( 'tabindex', cardLink ? '0' : '-1' );
			card.removeAttribute( 'aria-expanded' );

			if ( cardLink ) {
				card.setAttribute( 'aria-label', getCardTitle( card ) ? 'Read more about ' + getCardTitle( card ) : cardLink.textContent.trim() );
				return;
			}

			card.removeAttribute( 'aria-label' );
		} );
	};

	const toggleCard = function ( card ) {
		if ( ! touchQuery.matches ) {
			return;
		}

		const isFlipped = card.classList.toggle( 'is-flipped' );
		card.setAttribute( 'aria-expanded', isFlipped ? 'true' : 'false' );
	};

	cards.forEach( function ( card ) {
		card.addEventListener( 'click', function ( event ) {
			if ( event.target.closest( 'a' ) ) {
				return;
			}

			if ( ! touchQuery.matches ) {
				followCardLink( card );
				return;
			}

			toggleCard( card );
		} );

		card.addEventListener( 'keydown', function ( event ) {
			if ( touchQuery.matches && ( event.key === 'Enter' || event.key === ' ' ) ) {
				event.preventDefault();
				toggleCard( card );
				return;
			}

			if ( event.key === 'Enter' ) {
				followCardLink( card );
			}
		} );
	} );

	setMobileAttributes();
	touchQuery.addEventListener( 'change', setMobileAttributes );
}
